<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { PersonStores } from '@/store';
import GuardianTable from './components/GuardianTable.vue';
import ClinicianStudentTable from './components/ClinicianStudentTable.vue';
import StudentOrganizationTable from './components/StudentOrganizationTable.vue';
import ClinicianOrganizationTable from './components/ClinicianOrganizationTable.vue';
import ClinicianDisciplines from '@/views/Users/components/Connections/components/ClinicianDisciplines.vue';
import { PEOPLE_TYPES, PERMISSIONS } from '@/enum';
import StudentClinicianTable from './components/StudentClinicianTable.vue';
import StudentFacilitatorTable from '@/views/Users/components/Connections/components/StudentFacilitatorTable.vue';
import SchoolAdminOrganizationTable from '@/views/Users/components/Connections/components/SchoolAdminOrganizationTable.vue';
import ParentStudentTable from '@/views/Users/components/Connections/components/ParentStudentTable.vue';
import CaseManagerStudentTable from '@/views/Users/components/Connections/components/CaseManagerStudentTable.vue';
import StudentCaseManagerTable from '@/views/Users/components/Connections/components/StudentCaseManagerTable.vue';
import FacilitatorOrganizationTable from '@/views/Users/components/Connections/components/FacilitatorOrganizationTable.vue';
import BillingAdminOrganizationTable from '@/views/Users/components/Connections/components/BillingAdminOrganizationTable.vue';
import { useRoute } from 'vue-router';
import { can, usePersonTypes } from '@/composables';
import BuildingsList from './components/Buildings/BuildingsList.vue';
import BuildingsEdit from './components/Buildings/BuildingsEdit.vue';
import Grades from './components/Grades/Grades.vue';
import { useBuildingStore } from '@/store';

const route = useRoute();

onMounted(async () => {
  await refreshConnections();
  await buildingStore.fetchPersonBuildings(userId);
});

const profileStore = PersonStores.useUserProfileStore();
const connectionsStore = PersonStores.useUserConnectionsStore();
const buildingStore = useBuildingStore();
const isModalOpen = ref(false);
const selectedBuilding = ref(null);

const userId = Number(profileStore.userId || route.params.id);

const { organizations, flattenOrganizations, parents, students, clinicians, facilitators, loading } =
  storeToRefs(connectionsStore);

const isClinician = computed(() =>
  usePersonTypes.isClinicianOrCaseManager(profileStore.person?.people_type?.acronym as PEOPLE_TYPES),
);

const isStudent = computed(() => usePersonTypes.isStudent(profileStore.person?.people_type?.acronym as PEOPLE_TYPES));

const isSchoolAdmin = computed(() =>
  usePersonTypes.isSchoolAdmin(profileStore.person?.people_type?.acronym as PEOPLE_TYPES),
);

const isParent = computed(() => usePersonTypes.isParent(profileStore.person?.people_type?.acronym as PEOPLE_TYPES));

const isFacilitator = computed(() =>
  usePersonTypes.isFacilitator(profileStore.person?.people_type?.acronym as PEOPLE_TYPES),
);

const isBillingAdmin = computed(() =>
  usePersonTypes.isBillingAdmin(profileStore.person?.people_type?.acronym as PEOPLE_TYPES),
);

const isCaseManager = computed(() =>
  usePersonTypes.isCaseManager(profileStore.person?.people_type?.acronym as PEOPLE_TYPES),
);

const refreshConnections = async () => {
  if (can([PERMISSIONS.CONNECTIONS_INDEX])) await connectionsStore.getConnections();
};

const onEditBuilding = async (building) => {
  const loadedOrgId = buildingStore.organizationBuildings[0]?.organization_id;
  if (buildingStore.organizationBuildings.length === 0 || loadedOrgId !== building.organization_id) {
    await buildingStore.fetchOrganizationBuildings(building.organization_id);
  }

  selectedBuilding.value = building;
  isModalOpen.value = true;
};

const handleAttach = async (buildingIdArray: number[]) => {
  await buildingStore.attachPersonBuilding(
    {
      building_ids: buildingIdArray,
    },
    userId,
  );
  isModalOpen.value = false;
  buildingStore.doToast('Building attached successfully');
  await buildingStore.fetchPersonBuildings(userId);
};

const handleDetach = async (buildingIdArray: number[]) => {
  await buildingStore.detachPersonBuilding(
    {
      building_ids: buildingIdArray,
    },
    userId,
  );
  isModalOpen.value = false;
  buildingStore.doToast('Building detached successfully');
  await buildingStore.fetchPersonBuildings(userId);
};
</script>

<template>
  <div v-if="isClinician" class="w-full">
    <ClinicianOrganizationTable
      v-if="can([PERMISSIONS.ORGANIZATION_SHOW])"
      :table-data="organizations"
      :flat-organizations="flattenOrganizations"
      :clinician-id="userId"
      :loading="loading"
      :is-case-manager="isCaseManager"
      :case-manager-name="profileStore.person?.display_name"
      @update="refreshConnections"
    />
  </div>

  <div v-if="isStudent" class="w-full">
    <StudentOrganizationTable
      v-if="can([PERMISSIONS.STUDENT_ORGANIZATIONS_INDEX])"
      :table-data="organizations"
      :student-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
  </div>

  <div v-if="isSchoolAdmin && can([PERMISSIONS.SCHOOL_ADMIN_ORGANIZATIONS_INDEX])" class="w-full">
    <SchoolAdminOrganizationTable
      :table-data="organizations"
      :school-admin-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
  </div>

  <div v-if="isParent && can([PERMISSIONS.PARENT_STUDENTS_INDEX])" class="w-full">
    <ParentStudentTable :table-data="students" :parent-id="userId" :loading="loading" @update="refreshConnections" />
  </div>

  <div v-if="isCaseManager && can([PERMISSIONS.CASE_MANAGER_STUDENTS_INDEX])" class="w-full">
    <CaseManagerStudentTable
      :table-data="students"
      :case-manager-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
  </div>

  <div v-if="isFacilitator && can([PERMISSIONS.FACILITATOR_ORGANIZATIONS_INDEX])" class="w-full">
    <FacilitatorOrganizationTable
      :table-data="organizations"
      :facilitator-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
  </div>

  <div v-if="isBillingAdmin" class="w-full">
    <BillingAdminOrganizationTable
      :table-data="organizations"
      :billing-admin-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
  </div>

  <div v-if="!isParent && !isBillingAdmin && !isCaseManager" class="w-full">
    <BuildingsList
      :flatten-organizations="flattenOrganizations"
      :buildings="buildingStore.personBuildings || []"
      :total-records="buildingStore.totalRecords ?? 0"
      :loading="buildingStore.loading ?? false"
      @edit="onEditBuilding"
    />
    <BuildingsEdit
      v-model="isModalOpen"
      :selected-buildings="selectedBuilding?.buildings || []"
      :organization-buildings="buildingStore.organizationBuildings || []"
      :organization-name="selectedBuilding?.organization_name || ''"
      @attach="handleAttach"
      @detach="handleDetach"
    />
  </div>

  <div v-if="isClinician && !isCaseManager" class="w-full">
    <Grades :flatten-organizations="flattenOrganizations" :user-id="userId" />
    <ClinicianStudentTable
      v-if="can([PERMISSIONS.CLINICIAN_STUDENTS_INDEX])"
      :clinician-id="userId"
      :table-data="students"
      :loading="loading"
      @update="refreshConnections"
    />
    <ClinicianDisciplines v-if="can([PERMISSIONS.CLINICIAN_DISCIPLINES_INDEX])" :clinician-id="userId" />
  </div>

  <div v-if="isStudent" class="w-full">
    <Grades :flatten-organizations="flattenOrganizations" :user-id="userId" />
    <StudentClinicianTable
      v-if="can([PERMISSIONS.STUDENT_CLINICIANS_INDEX])"
      :table-data="clinicians"
      :student-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
    <StudentFacilitatorTable
      v-if="can([PERMISSIONS.STUDENT_FACILITATORS_INDEX])"
      :table-data="facilitators"
      :student-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
    <GuardianTable
      v-if="can([PERMISSIONS.STUDENT_PARENTS_INDEX])"
      :table-data="parents"
      :student-id="userId"
      :loading="loading"
      @update="refreshConnections"
    />
    <StudentCaseManagerTable v-if="can([PERMISSIONS.STUDENT_CASE_MANAGERS_INDEX])" :student-id="userId" />
  </div>
</template>
